import React, { useCallback, useEffect, useState } from 'react';
import { Hospital, Plus, X, Edit2, Trash2, BadgeCheck, Search, Loader2 } from 'lucide-react';
import api from '@/lib/api';

/**
 * HospitalAttachmentsSection — embedded inside the "Locations" tab.
 *
 * Lets the doctor manage where they perform major procedures — distinct from
 * clinics (consultation-only).
 *
 * Master vs DpD:
 *   - Picks from `/api/public/hospital-master` autocomplete → source=verified,
 *     accreditation badges (NABH/JCI) appear on the public profile.
 *   - Free-text → source=DpD, profile shows a small "DpD" pill until DocSite
 *     verifies and adds to master.
 */
const ROLES = [
  { value: 'consultant', label: 'Consultant' },
  { value: 'attached', label: 'Attached' },
  { value: 'visiting_surgeon', label: 'Visiting surgeon' },
  { value: 'in_house', label: 'In-house team' },
];

const ROLE_LABEL = ROLES.reduce((m, r) => ({ ...m, [r.value]: r.label }), {});

export default function HospitalAttachmentsSection() {
  const [items, setItems] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [att, procs] = await Promise.all([
        api.get('/dashboard/hospital-attachments'),
        api.get('/dashboard/procedures'),
      ]);
      setItems(att.data || []);
      setProcedures(procs.data || []);
    } catch {/* show empty state */} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSaved = () => { setShowForm(false); setEditing(null); load(); };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this hospital attachment? It will disappear from your public profile.')) return;
    await api.delete(`/dashboard/hospital-attachments/${id}`);
    load();
  };

  return (
    <div className="mt-12 pt-8 border-t border-slate-200" data-testid="dash-hospitals-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Hospital size={22} className="text-sky-600" /> Hospital Infra · Surgical Attachments
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Hospitals where you perform major procedures or surgeries. Each entry shows on your public profile with accreditation badges and the procedures you do there.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700"
                data-testid="dash-hospital-add-btn">
          <Plus size={16} /> Add hospital
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 mt-4">Loading hospital attachments…</div>
      ) : items.length === 0 ? (
        <div className="mt-6 rounded-xl border-2 border-dashed border-slate-200 p-8 text-center">
          <Hospital size={32} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No hospital attachments yet. Add the hospitals where you operate or do interventional procedures.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          {items.map(h => (
            <div key={h.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`dash-hospital-row-${h.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900 truncate">{h.hospital_name}</p>
                    {h.source === 'DpD' && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-300" title="Doctor-Provided Data — pending DocSite verification">DpD</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{h.city} · {ROLE_LABEL[h.role] || h.role}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => { setEditing(h); setShowForm(true); }}
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700"
                          data-testid={`dash-hospital-edit-${h.id}`}
                          title="Edit">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(h.id)}
                          className="p-1.5 hover:bg-red-50 rounded text-slate-500 hover:text-red-600"
                          data-testid={`dash-hospital-delete-${h.id}`}
                          title="Remove">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {(h.accreditation || []).length > 0 && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {h.accreditation.map(a => (
                    <span key={a} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <BadgeCheck size={10} /> {a}
                    </span>
                  ))}
                </div>
              )}
              {(h.procedure_ids || []).length > 0 && (
                <p className="text-xs text-slate-500 mt-2">
                  {h.procedure_ids.length} procedure{h.procedure_ids.length !== 1 ? 's' : ''} mapped here
                </p>
              )}
              {h.team_note && (
                <p className="text-xs text-slate-600 italic mt-1.5 leading-relaxed">{h.team_note}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <HospitalForm
          existing={editing}
          procedures={procedures}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

// ============================ Add / Edit form ============================

function HospitalForm({ existing, procedures, onClose, onSaved }) {
  // Close on Escape key — matches native modal behaviour on shadcn dialogs.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [form, setForm] = useState({
    master_id: existing?.master_id || null,
    hospital_name: existing?.hospital_name || '',
    city: existing?.city || '',
    role: existing?.role || 'consultant',
    procedure_ids: existing?.procedure_ids || [],
    team_note: existing?.team_note || '',
    team_size: existing?.team_size || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.hospital_name?.trim() || form.hospital_name.length < 2) {
      setError('Hospital name is required.'); return;
    }
    if (!form.city?.trim() || form.city.length < 2) {
      setError('City is required.'); return;
    }
    setSaving(true);
    try {
      const body = {
        master_id: form.master_id || null,
        hospital_name: form.hospital_name.trim(),
        city: form.city.trim(),
        role: form.role,
        procedure_ids: form.procedure_ids,
        team_note: form.team_note?.trim() || null,
        team_size: form.team_size === '' ? null : Number(form.team_size),
        display_order: existing?.display_order || 0,
      };
      if (existing) {
        await api.put(`/dashboard/hospital-attachments/${existing.id}`, body);
      } else {
        await api.post('/dashboard/hospital-attachments', body);
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const toggleProc = (pid) => {
    setForm(f => ({
      ...f,
      procedure_ids: f.procedure_ids.includes(pid)
        ? f.procedure_ids.filter(x => x !== pid)
        : [...f.procedure_ids, pid],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="dash-hospital-form">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">
            {existing ? 'Edit hospital attachment' : 'Add hospital attachment'}
          </h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>}

          <HospitalAutocomplete
            value={form.hospital_name}
            cityValue={form.city}
            onPickMaster={(m) => setForm(f => ({ ...f, master_id: m.id, hospital_name: m.name, city: m.city }))}
            onTypeOwn={(name) => setForm(f => ({ ...f, master_id: null, hospital_name: name }))}
          />

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">City</label>
            <input
              type="text" value={form.city} onChange={e => update('city', e.target.value)}
              placeholder="Mumbai"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
              data-testid="dash-hospital-city"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Your role at this hospital</label>
            <select value={form.role} onChange={e => update('role', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:border-sky-500"
                    data-testid="dash-hospital-role">
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">
              Procedures performed here · {form.procedure_ids.length} selected
            </label>
            {procedures.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Add procedures in the Procedures tab first to map them to this hospital.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto p-2 bg-slate-50 rounded-lg" data-testid="dash-hospital-procs">
                {procedures.map(p => (
                  <label key={p.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-white cursor-pointer">
                    <input type="checkbox" className="mt-0.5"
                           checked={form.procedure_ids.includes(p.id)}
                           onChange={() => toggleProc(p.id)}
                           data-testid={`dash-hospital-proc-${p.id}`} />
                    <span className="text-sm text-slate-700">{p.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Team note · optional</label>
              <input type="text" value={form.team_note} onChange={e => update('team_note', e.target.value)}
                     placeholder="5-member dedicated cardiac team"
                     maxLength={240}
                     className="w-full px-3 py-2.5 border border-slate-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Team size · optional</label>
              <input type="number" min="0" max="999" value={form.team_size}
                     onChange={e => update('team_size', e.target.value)}
                     className="w-full px-3 py-2.5 border border-slate-300 rounded-lg" />
            </div>
          </div>

          <p className="text-xs text-slate-500 leading-relaxed">
            Note: Team details are stored privately and not currently shown on your public profile (v1). Only hospital name, accreditation, and the procedures you do there are public.
          </p>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg">
            Cancel
          </button>
          <button type="submit" disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 disabled:opacity-60"
                  data-testid="dash-hospital-save">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {existing ? 'Save changes' : 'Add hospital'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================ Master autocomplete ============================

function HospitalAutocomplete({ value, onPickMaster, onTypeOwn }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!showSuggest) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get('/public/hospital-master', { params: { q: query, limit: 8 } });
        setResults(r.data || []);
      } catch { setResults([]); } finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [query, showSuggest]);

  return (
    <div className="relative">
      <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5">Hospital name</label>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text"
               value={query}
               onChange={(e) => { setQuery(e.target.value); onTypeOwn(e.target.value); setShowSuggest(true); }}
               onFocus={() => setShowSuggest(true)}
               onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
               placeholder="Start typing — Lilavati, Apollo, AIIMS, Manipal…"
               className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-lg focus:border-sky-500 focus:ring-1 focus:ring-sky-500"
               data-testid="dash-hospital-name" />
      </div>
      {showSuggest && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="dash-hospital-suggest">
          {searching ? (
            <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-xs">
              <p className="text-slate-700 font-semibold">No match — keep typing &amp; we'll save as <span className="font-bold">DpD</span> (Doctor-Provided Data)</p>
              <p className="text-slate-500 mt-1">DocSite will verify and promote it into the master list within 48 hrs.</p>
            </div>
          ) : (
            results.map(r => (
              <button key={r.id} type="button"
                      onMouseDown={(e) => { e.preventDefault(); onPickMaster(r); setQuery(r.name); setShowSuggest(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-sky-50 border-b border-slate-100 last:border-b-0"
                      data-testid={`dash-hospital-suggest-${r.id}`}>
                <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-500">{r.city}</span>
                  {(r.accreditation || []).length > 0 && (
                    <div className="flex gap-1">
                      {r.accreditation.map(a => (
                        <span key={a} className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{a}</span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
